import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import Tooltip from '@/components/Tooltip';
import {
  InfoAside,
  SuccessAside,
  WarningAside,
} from '@/components/Aside/Aside';

interface Props {
  definition: string;
  children: React.ReactNode;
}

const Term = ({ definition, children }: Props) => {
  return (
    <Tooltip
      content={<ContentWrapper>{definition}</ContentWrapper>}
      direction="n"
    >
      <Wrapper>
        {children}
        <Trigger>?</Trigger>
      </Wrapper>
    </Tooltip>
  );
};

const Wrapper = styled.span`
  display: inline-block;
  cursor: help;
  padding-right: 2px;

  /* On iOS, bafflingly, the asterisk can only be tapped once. After that, it keeps trying to select it, and then opening the iOS selection tooltip in front of ours. Solve this by making the asterisk unselectable on smaller windows. */
  @media ${BREAKPOINTS.mdAndSmaller} {
    user-select: none;
  }
`;

const Trigger = styled.span`
  display: inline-block;
  font-size: 0.65em;
  font-weight: var(--font-weight-bold);
  color: var(--color-secondary);
  transform: translate(10%, -30%);

  /*
    When definitions are used within asides, we want to overwrite the colors. Honestly there's not a lot of method to the madness here, I'm just picking colors that look best across light/dark.
  */
  html[data-color-mode='light'] ${InfoAside} & {
    color: var(--color-primary);
  }
  html[data-color-mode='light'] ${SuccessAside} & {
    color: var(--color-success-500);
    /* Same color, but brighter / richer */
    color: oklch(0.52 0.22 146.29);
  }
  html[data-color-mode='dark'] ${InfoAside} & {
    color: var(--color-info-700);
  }
  html[data-color-mode='dark'] ${SuccessAside} &,
  html[data-color-mode='dark'] ${WarningAside} & {
    color: var(--color-primary);
  }
`;

const ContentWrapper = styled.span`
  --color-primary: black;
  display: block;
  font-size: 1rem;
  line-height: 1.5;
`;

export default Term;
